// ─────────────────────────────────────────────────────────────
// SharedFilesPanel — 작업파일 공유란 (메모 게시판 우측)
// v7.312
//
// 이미지 / zip / pdf / docx / xlsx 등 아무 파일이나 올리고 받는다.
// 실체는 nginx WebDAV(/attachments/shared/), 메타는 shared_files 테이블.
//
// App.js 에 넣지 않고 파일을 분리한 이유: App.js 가 이미 23,000줄이라
// 여기에 200줄을 더 얹으면 메모 탭 근처를 다시 손볼 때 앵커 잡기가 어려워진다.
// (다른 화면들 — MobileScan / EventCalendar 등 — 과 같은 방식이다)
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SharedFileService } from './services/SharedFileService';
import { AttachmentService } from './services/AttachmentService';

function Icon({ name, size = 20, style }) {
  return <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...style }}>{name}</span>;
}

const SHARED_DIR = 'shared';

// 확장자 → 아이콘/색. 목록에서 종류를 한눈에 구분하려는 용도다.
const FILE_KINDS = [
  { re: /\.(png|jpe?g|gif|webp|bmp|svg|heic)$/i, icon: 'image',        color: '#2e7d32', label: '이미지' },
  { re: /\.(zip|7z|rar|tar|gz)$/i,               icon: 'folder_zip',   color: '#6d4c41', label: '압축' },
  { re: /\.pdf$/i,                               icon: 'picture_as_pdf',color: '#c62828', label: 'PDF' },
  { re: /\.(xlsx?|xlsm|csv|tsv)$/i,              icon: 'table',        color: '#1b5e20', label: '엑셀' },
  { re: /\.(docx?|rtf|hwpx?|odt)$/i,             icon: 'description',  color: '#1565c0', label: '문서' },
  { re: /\.(pptx?|key|odp)$/i,                   icon: 'slideshow',    color: '#e65100', label: '슬라이드' },
  { re: /\.(mp4|mov|avi|mkv|webm)$/i,            icon: 'movie',        color: '#4527a0', label: '영상' },
  { re: /\.(txt|md|log|json|xml|ya?ml)$/i,       icon: 'article',      color: '#455a64', label: '텍스트' },
];
const kindOf = (name) => FILE_KINDS.find(k => k.re.test(name || '')) || { icon: 'draft', color: '#78909c', label: '파일' };
const isImage = (name) => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name || '');

const fmtSize = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
  if (b < 1024 * 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
  return (b / 1024 / 1024 / 1024).toFixed(2) + ' GB';
};
const fmtDate = (v) => {
  if (!v) return '-';
  try {
    return new Date(v).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch (e) { return '-'; }
};

// v7.313: 사이드 패널(width 330) → 탭 본문(fullWidth) 두 가지 모양을 모두 지원한다.
//   onCount 는 탭 뱃지에 건수를 띄우려고 부모(App.js)로 올려 보내는 콜백이다.
export default function SharedFilesPanel({ staffList = [], connected = true, fullWidth = false, onCount }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(true);      // shared_files 테이블 존재 여부
  const [errMsg, setErrMsg] = useState('');
  const [uploading, setUploading] = useState(null); // { done, total, name }
  const [dragOver, setDragOver] = useState(false);
  const [author, setAuthor] = useState(() => {
    try { return localStorage.getItem('inventura_share_author') || ''; } catch (e) { return ''; }
  });
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!connected) return;
    setLoading(true);
    const res = await SharedFileService.list();
    setLoading(false);
    if (!res.ok) {
      setReady(false);
      setErrMsg(res.msg || '');
      setFiles([]);
      return;
    }
    setReady(true);
    setErrMsg('');
    setFiles(res.data);
  }, [connected]);

  useEffect(() => { load(); }, [load]);
  // 목록이 바뀔 때마다 부모에게 건수를 알린다 (탭 뱃지용)
  useEffect(() => { if (onCount) onCount(files.length); }, [files.length, onCount]);

  const doUpload = async (fileList) => {
    const arr = Array.from(fileList || []).filter(Boolean);
    if (arr.length === 0) return;   // 취소했거나 빈 선택
    for (let i = 0; i < arr.length; i++) {
      const f = arr[i];
      setUploading({ done: i, total: arr.length, name: f.name });
      const up = await AttachmentService.uploadToDir(SHARED_DIR, f, author);
      if (!up.ok) {
        setUploading(null);
        const m = String(up.error?.message || '');
        let hint = '※ 브라우저 개발자도구 Network 탭에서 /attachments/ 요청의 상태코드를 확인해 주세요.';
        if (/HTTP 405/.test(m)) hint = '※ nginx 가 PUT 을 안 받고 있습니다. /attachments/ 블록의 dav_methods 설정을 확인해 주세요.';
        else if (/HTTP 413/.test(m)) hint = '※ 파일이 client_max_body_size(2G)를 넘었습니다.';
        else if (/HTTP 40[13]/.test(m)) hint = '※ attachments 폴더 쓰기 권한을 확인해 주세요.';
        else if (/HTTP 409|HTTP 500/.test(m)) hint = '※ shared 하위 폴더 생성에 실패했을 수 있습니다. create_full_put_path on 설정을 확인해 주세요.';
        alert(`"${f.name}" 업로드 실패\n\n${m || '알 수 없는 오류'}\n\n${hint}`);
        return;
      }
      const rec = await SharedFileService.create({
        name: up.data.name, path: up.data.path, size: up.data.size,
        mime: up.data.mime, uploadedBy: author,
      });
      if (!rec.ok) {
        // DB 기록에 실패하면 방금 올린 파일은 목록에 안 잡히는 고아가 된다 → 되돌린다
        await AttachmentService.remove(up.data.path);
        setUploading(null);
        alert('목록 저장 실패: ' + rec.msg
          + '\n\n※ shared_files 테이블이 없으면 migrations/v7.312-memo-images-shared-files.sql 을 먼저 실행해 주세요.');
        return;
      }
    }
    setUploading(null);
    load();
  };

  const doDelete = async (f) => {
    if (!window.confirm(`"${f.name}" 을(를) 삭제할까요?\n\n파일과 목록이 모두 지워집니다.`)) return;
    const res = await SharedFileService.remove(f.id);
    if (!res.ok) { alert('삭제 실패: ' + res.msg); return; }
    await AttachmentService.remove(f.path);   // 404 는 성공 취급
    load();
  };

  const totalSize = files.reduce((s, f) => s + (Number(f.size) || 0), 0);

  return (
    <div style={{
      ...(fullWidth
        ? { width: '100%' }
        : { width: 330, flexShrink: 0, alignSelf: 'flex-start', maxHeight: 760 }),
      background: 'rgba(255,251,240,0.94)', backdropFilter: 'blur(8px)',
      border: '2px solid rgba(93,64,55,0.35)', borderRadius: 14,
      boxShadow: '0 6px 18px rgba(0,0,0,0.18)', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* 헤더 — 탭 본문일 때는 탭 자체가 제목 역할을 하므로 새로고침만 남긴다 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', background: 'rgba(93,64,55,0.9)', color: '#fff5e6' }}>
        <Icon name="folder_shared" size={18} style={{ color: '#ffd180' }} />
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.03em' }}>업로드된 작업파일</span>
        <span style={{ padding: '1px 8px', borderRadius: 9999, background: 'rgba(255,235,180,0.25)', fontSize: 11, fontWeight: 700 }}>{files.length}건</span>
        <div style={{ flex: 1 }} />
        {ready && files.length > 0 && (
          <span style={{ fontSize: 11, opacity: 0.85, marginRight: 4 }}>{fmtSize(totalSize)}</span>
        )}
        <button onClick={load} disabled={loading} title="새로고침"
          style={{ width: 26, height: 26, border: 'none', background: 'transparent', color: '#ffd180', cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
          <Icon name={loading ? 'hourglass_empty' : 'refresh'} size={16} />
        </button>
      </div>

      {/* 올린 사람 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderBottom: '1px solid rgba(93,64,55,0.15)' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6d4c41' }}>올린 사람</span>
        <select value={author} onChange={e => { setAuthor(e.target.value); try { localStorage.setItem('inventura_share_author', e.target.value); } catch (err) {} }}
          style={{ flex: 1, padding: '5px 8px', border: '1px solid rgba(93,64,55,0.3)', borderRadius: 7, fontSize: 12, background: '#fff', outline: 'none', cursor: 'pointer' }}>
          <option value="">(선택 안 함)</option>
          {staffList.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* 업로드 영역 — 클릭 또는 드래그앤드롭 */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer?.files); }}
        onClick={() => !uploading && inputRef.current?.click()}
        style={{
          margin: '10px 12px', padding: fullWidth ? '26px 12px' : '14px 12px', borderRadius: 10, cursor: uploading ? 'wait' : 'pointer',
          border: `2px dashed ${dragOver ? '#f57c00' : 'rgba(93,64,55,0.35)'}`,
          background: dragOver ? 'rgba(245,124,0,0.08)' : 'rgba(255,255,255,0.6)',
          textAlign: 'center', transition: 'all 0.15s',
        }}>
        <input ref={inputRef} type="file" multiple style={{ display: 'none' }}
          onClick={e => e.stopPropagation()}
          onChange={e => { const fl = Array.from(e.target.files || []); e.target.value = ''; doUpload(fl); }} />
        {uploading ? (
          <div style={{ fontSize: 12, color: '#6d4c41', fontWeight: 700 }}>
            <Icon name="cloud_upload" size={20} style={{ color: '#f57c00', display: 'block', margin: '0 auto 4px' }} />
            업로드 중 {uploading.done + 1}/{uploading.total}
            <div style={{ fontSize: 10.5, fontWeight: 500, marginTop: 2, color: '#8d6e63', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{uploading.name}</div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: '#6d4c41', fontWeight: 700 }}>
            <Icon name="upload_file" size={20} style={{ color: '#f57c00', display: 'block', margin: '0 auto 4px' }} />
            파일을 끌어다 놓거나 클릭
            <div style={{ fontSize: 10, fontWeight: 500, marginTop: 3, color: '#8d6e63' }}>이미지 · zip · pdf · 워드 · 엑셀 — 형식 제한 없음</div>
          </div>
        )}
      </div>

      {/* 목록 */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
        {!ready ? (
          <div style={{ padding: '18px 10px', fontSize: 11.5, color: '#8d6e63', lineHeight: 1.6, background: 'rgba(198,40,40,0.06)', border: '1px solid rgba(198,40,40,0.2)', borderRadius: 8 }}>
            <strong style={{ color: '#c62828' }}>shared_files 테이블이 없습니다.</strong><br />
            Supabase SQL Editor 에서<br />
            <code style={{ fontSize: 10.5 }}>migrations/v7.312-memo-images-shared-files.sql</code><br />
            을 실행한 뒤 새로고침해 주세요.
            {errMsg && <div style={{ marginTop: 6, fontSize: 10, color: '#a1887f' }}>{errMsg}</div>}
          </div>
        ) : files.length === 0 ? (
          <div style={{ padding: '24px 10px', textAlign: 'center', fontSize: 12, color: '#a1887f' }}>
            아직 공유된 파일이 없습니다
          </div>
        ) : (
          <div style={fullWidth
            ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 8 }
            : { display: 'flex', flexDirection: 'column', gap: 6 }}>
            {files.map(f => {
              const k = kindOf(f.name);
              const url = AttachmentService.getPublicUrl(f.path);
              return (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fff', border: '1px solid rgba(93,64,55,0.18)', borderRadius: 9 }}>
                  {isImage(f.name) ? (
                    <img src={url} alt={f.name} title="클릭하면 원본"
                      onClick={() => window.open(url, '_blank', 'noopener')}
                      style={{ width: fullWidth ? 46 : 34, height: fullWidth ? 46 : 34, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', flexShrink: 0, cursor: 'zoom-in' }} />
                  ) : (
                    <Icon name={k.icon} size={fullWidth ? 32 : 26} style={{ color: k.color, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div title={f.name} style={{ fontSize: 12, fontWeight: 700, color: '#3e2723', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                    <div style={{ fontSize: 10, color: '#8d6e63', marginTop: 1 }}>
                      {fmtSize(f.size)}
                      {f.uploaded_by ? ' · ' + f.uploaded_by : ''}
                      {' · ' + fmtDate(f.created_at)}
                    </div>
                  </div>
                  {/* download 속성은 동일 출처라 원본 한글 파일명 그대로 저장된다 */}
                  <a href={url} download={f.name} title="다운로드"
                    style={{ width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1565c0', flexShrink: 0, textDecoration: 'none' }}>
                    <Icon name="download" size={17} />
                  </a>
                  <button onClick={() => doDelete(f)} title="삭제"
                    style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: 'transparent', color: '#c62828', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}>
                    <Icon name="delete" size={17} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!fullWidth && ready && files.length > 0 && (
        <div style={{ padding: '8px 14px', borderTop: '1px solid rgba(93,64,55,0.15)', fontSize: 10.5, color: '#8d6e63', display: 'flex', justifyContent: 'space-between' }}>
          <span>총 {files.length}개</span>
          <span>{fmtSize(totalSize)}</span>
        </div>
      )}
    </div>
  );
}
